import { useState, useMemo } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { PageContainer } from "../../../../components/common/page-container";
import { Heading, Text } from "../../../../components/common/typography";
import { ShimmerLoader, Shimmer } from "../../../../components/common/shimmer/shimmer";
import { AccentStatCard } from "../../../../components/common/accent-stat-card/accent-stat-card";
import { SimpleTable } from "../../../../components/common/simple-table/simple-table";
import { Pagination } from "../../../../components/common/pagination/pagination";
import { SearchBar } from "../../../../components/common/input";
import { SegmentedControl } from "../../../../components/common/segmented-control";
import { Button } from "../../../../components/common/button";
import { useLoyaltyConfig } from "../../hooks/useLoyaltyConfig";
import { useAdminLoyaltyStats } from "../../hooks/useAdminLoyaltyStats";
import { useAdminLoyaltyTransactions } from "../../hooks/useAdminLoyaltyTransactions";
import {
  useAdminLoyaltyRedemptions,
  type AdminLoyaltyRedemptionDto,
} from "../../hooks/useAdminLoyaltyRedemptions";
import { buildAdminLoyaltyTableColumns } from "../components/admin-loyalty-table-columns";
import { AppRoutes } from "../../../../common/routes/app_routes";
import type { AdminLoyaltyTransactionDto } from "../../../../api";

type TableView = "transactions" | "redemptions";

const TABLE_VIEW_OPTIONS: { value: TableView; label: string }[] = [
  { value: "transactions", label: "Transactions" },
  { value: "redemptions", label: "Redemptions" },
];

const PAGE_SIZE = 10;

export function AdminLoyaltyConfigPage() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState("");
  const [txPage, setTxPage] = useState(1);
  const [rdPage, setRdPage] = useState(1);

  const tabParam = searchParams.get("tab");
  const tableView: TableView = tabParam === "redemptions" ? "redemptions" : "transactions";

  const setTableView = (next: TableView) => {
    const p = new URLSearchParams(searchParams);
    if (next === "transactions") p.delete("tab");
    else p.set("tab", next);
    setSearchParams(p, { replace: true });
    setTxPage(1);
    setRdPage(1);
  };

  const { data: config, isLoading: configLoading } = useLoyaltyConfig();
  const { data: stats, isLoading: statsLoading } = useAdminLoyaltyStats();
  const { data: transactionsData, isLoading: txLoading } =
    useAdminLoyaltyTransactions({ page: txPage, pageSize: PAGE_SIZE, search: search.trim() || undefined });
  const { data: redemptionsData, isLoading: rdLoading } =
    useAdminLoyaltyRedemptions({ page: rdPage, pageSize: PAGE_SIZE, search: search.trim() || undefined });

  const txColumns = useMemo(
    () => buildAdminLoyaltyTableColumns((tx: AdminLoyaltyTransactionDto) =>
      navigate(
        AppRoutes.ADMIN_LOYALTY_TRANSACTION.replace(":id", tx.id),
        { state: { transaction: tx } },
      )
    ),
    [navigate],
  );

  const isInitialLoading = configLoading || statsLoading;

  if (isInitialLoading) {
    return (
      <PageContainer>
        <ShimmerLoader message="Loading loyalty data…">
          <div className="flex flex-col gap-4">
            <Shimmer className="h-8 w-48" />
            <div className="grid grid-cols-3 gap-4">
              <Shimmer className="h-24 rounded-2xl" />
              <Shimmer className="h-24 rounded-2xl" />
              <Shimmer className="h-24 rounded-2xl" />
            </div>
            <div className="grid grid-cols-2 gap-6">
              <Shimmer className="h-56 rounded-xl" />
              <Shimmer className="h-56 rounded-xl" />
            </div>
            <Shimmer className="h-80 rounded-xl" />
          </div>
        </ShimmerLoader>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <div className="flex flex-col gap-6">
        <Heading variant="h2" tone="dark">
          Loyalty Points
        </Heading>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <AccentStatCard
            label="Total Points Issued"
            value={(stats?.totalPointsIssued ?? 0).toLocaleString()}
            accent="green"
          />
          <AccentStatCard
            label="Active Members"
            value={(stats?.activeMembers ?? 0).toLocaleString()}
            accent="green"
          />
          <AccentStatCard
            label="Points Redeemed"
            value={(stats?.pointsRedeemed ?? 0).toLocaleString()}
            accent="green"
          />
        </div>

        <div className="flex items-center gap-3 max-md:flex-col max-md:items-stretch">
          <div className="flex-1">
            <SearchBar
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setTxPage(1);
                setRdPage(1);
              }}
              placeholder="Search"
              wrapperClassName=""
            />
          </div>
          <SegmentedControl
            options={TABLE_VIEW_OPTIONS}
            value={tableView}
            onChange={setTableView}
            aria-label="Toggle table view"
            className="max-md:w-full max-md:justify-between"
          />
        </div>

        {tableView === "transactions" ? (
          txLoading ? (
            <ShimmerLoader message="Fetching loyalty transactions…">
              <Shimmer className="h-64 rounded-xl" />
            </ShimmerLoader>
          ) : (
            <SimpleTable
              columns={txColumns}
              data={transactionsData?.data ?? []}
              keyExtractor={(t) => t.id}
              onRowClick={(t) => navigate(
                AppRoutes.ADMIN_LOYALTY_TRANSACTION.replace(":id", t.id),
                { state: { transaction: t } },
              )}
              footer={
                transactionsData?.meta && (transactionsData.meta.pageCount ?? 1) > 1 ? (
                  <div className="flex justify-center py-2">
                    <Pagination
                      page={transactionsData.meta.currentPage}
                      pageCount={transactionsData.meta.pageCount ?? 1}
                      onPageChange={setTxPage}
                    />
                  </div>
                ) : undefined
              }
            />
          )
        ) : (
          <AdminRedemptionsPanel
            data={redemptionsData?.data ?? []}
            isLoading={rdLoading}
            page={rdPage}
            pageCount={redemptionsData?.meta?.pageCount ?? 1}
            currentPage={redemptionsData?.meta?.currentPage ?? 1}
            onPageChange={setRdPage}
            redemptionRate={config?.redemptionValuePer100Points ?? 0}
            onView={(rd) => navigate(
              AppRoutes.ADMIN_LOYALTY_REDEMPTION.replace(":id", rd.id),
              { state: { redemption: rd } },
            )}
          />
        )}
      </div>
    </PageContainer>
  );
}

interface AdminRedemptionsPanelProps {
  data: AdminLoyaltyRedemptionDto[];
  isLoading: boolean;
  page: number;
  pageCount: number;
  currentPage: number;
  onPageChange: (p: number) => void;
  redemptionRate: number;
  onView: (rd: AdminLoyaltyRedemptionDto) => void;
}

function AdminRedemptionsPanel({ data: rows, isLoading, pageCount, currentPage, onPageChange, redemptionRate, onView }: AdminRedemptionsPanelProps) {
  return (
    <div className="flex flex-col gap-3">
      {isLoading ? (
        <ShimmerLoader message="Fetching redemptions…">
          <Shimmer className="h-32 rounded-xl" />
        </ShimmerLoader>
      ) : rows.length === 0 ? (
        <div className="rounded-2xl border border-borderDefault bg-bgWhite px-6 py-5">
          <Text variant="bodySm" tone="muted">No redemptions yet.</Text>
        </div>
      ) : (
        <div className="overflow-hidden rounded-xl border border-borderDefault bg-bgWhite">
          <div className="grid grid-cols-[1.5fr_1fr_1fr_0.8fr_0.8fr_0.8fr_0.5fr] gap-6 bg-bgLight px-6 py-3 max-md:hidden">
            <Text variant="capsBig" tone="muted">USER</Text>
            <Text variant="capsBig" tone="muted">ORDER</Text>
            <Text variant="capsBig" tone="muted">DATE</Text>
            <Text variant="capsBig" tone="muted">STATUS</Text>
            <Text variant="capsBig" tone="muted" className="text-right">POINTS</Text>
            <Text variant="capsBig" tone="muted" className="text-right">DISCOUNT</Text>
            <Text variant="capsBig" tone="muted" className="text-right">ACTIONS</Text>
          </div>
          {rows.map((r) => (
            <div
              key={r.id}
              // Phones: a card — user on top, the other fields two-up with labels.
              className="grid grid-cols-[1.5fr_1fr_1fr_0.8fr_0.8fr_0.8fr_0.5fr] gap-6 items-center border-t border-borderDefault px-6 py-4 cursor-pointer transition-colors hover:bg-bgLight max-md:grid-cols-2 max-md:gap-x-4 max-md:gap-y-3 max-md:px-4"
              onClick={() => onView(r)}
            >
              <div className="flex flex-col gap-0.5 min-w-0 max-md:col-span-2">
                <Text variant="bodyBold" tone="dark" className="truncate">
                  {r.user.companyName ?? r.user.name}
                </Text>
                <Text variant="bodyXs" tone="muted" className="truncate">
                  {r.user.email ?? r.user.phoneNumber ?? ""}
                </Text>
              </div>
              <div>
                <PhoneLabel>Order</PhoneLabel>
                {r.saleOrderId != null ? (
                  <Link to={`/admin/sales/${r.saleOrderId}`} className="text-accentGreenDark underline-offset-2 hover:underline">
                    <Text variant="body" tone="inherit">#{r.saleOrderId}</Text>
                  </Link>
                ) : (
                  <Text variant="body" tone="muted">{r.reason ?? "—"}</Text>
                )}
              </div>
              <div>
                <PhoneLabel>Date</PhoneLabel>
                <Text variant="body" tone="body">
                  {new Date(r.createdAt).toLocaleDateString("en-GB")}
                </Text>
              </div>
              <div>
                <PhoneLabel>Status</PhoneLabel>
                <Text variant="bodySm" tone={r.isReversed ? "muted" : "greenDark"}>
                  {r.isReversed ? "Reversed" : "Active"}
                </Text>
              </div>
              <div>
                <PhoneLabel>Points</PhoneLabel>
                <Text variant="bodyBold" tone="dark" className="text-right max-md:text-left">
                  -{r.points.toLocaleString()}
                </Text>
              </div>
              <div>
                <PhoneLabel>Discount</PhoneLabel>
                <Text variant="bodyBold" tone="dark" className="text-right max-md:text-left">
                  QAR {((r.points / 100) * redemptionRate).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </Text>
              </div>
              <div className="flex justify-end max-md:col-span-2">
                <Button
                  variant="ghost"
                  size="sm"
                  className="!px-0 !text-textDark"
                  onClick={(e) => { e.stopPropagation(); onView(r); }}
                >
                  View
                </Button>
              </div>
            </div>
          ))}
          {pageCount > 1 && (
            <div className="flex justify-center border-t border-borderDefault px-6 py-4">
              <Pagination page={currentPage} pageCount={pageCount} onPageChange={onPageChange} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** Field name shown above a value only on phones, where the header row is hidden. */
function PhoneLabel({ children }: { children: string }) {
  return (
    <Text variant="capsSm" tone="muted" className="md:hidden">
      {children}
    </Text>
  );
}
